import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Lock, CheckCircle, AlertCircle, Eye, EyeOff } from "lucide-react";
import { GoldButton, Logo, inputClass, labelClass } from "../components/shared/UIComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Reset failed. The link may have expired.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-emerald-950 flex items-center justify-center" data-testid="reset-password-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(229,201,139,0.06),transparent_60%)]" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-md px-4 sm:px-6">
        <div className="text-center mb-8">
          <Logo size="small" showTagline={false} />
        </div>

        {success ? (
          <div className="text-center" data-testid="reset-success">
            <div className="mx-auto h-20 w-20 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mb-6">
              <CheckCircle className="h-10 w-10 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Password Reset!</h2>
            <p className="text-sm text-cream/60 mb-6">Your password has been updated. You can now sign in with your new password.</p>
            <GoldButton onClick={onDone} data-testid="reset-go-login">Sign In</GoldButton>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto h-14 w-14 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center mb-4">
                <Lock className="h-7 w-7 text-gold" />
              </div>
              <h2 className="text-2xl font-bold text-white">Create New Password</h2>
              <p className="text-sm text-cream/50 mt-2">Enter your new password below.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <label className="block">
                <span className={labelClass}>New Password</span>
                <div className="relative">
                  <input required type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                    minLength={8} className={inputClass + " pr-12"} placeholder="Min 8 characters" data-testid="reset-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-cream/40 hover:text-cream/70">
                    {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </label>
              <label className="block">
                <span className={labelClass}>Confirm Password</span>
                <input required type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  minLength={8} className={inputClass} placeholder="Confirm your password" data-testid="reset-confirm" />
              </label>
              {error && (
                <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-300 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                </div>
              )}
              <GoldButton type="submit" disabled={loading} className="w-full !rounded-xl" data-testid="reset-submit">
                {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Resetting...</span> : "Reset Password"}
              </GoldButton>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
